import { Input, TextArea, CheckBox, DatePicker, Select, Option, Text } from '@ui5/webcomponents-react';
import type { FieldDef, MetaField } from '@b1links/core';
import type { EntityMeta, Rec } from '../api/client';
import { enumFor, enumLabel, formatValue, interpolate } from './format';
import { LookupInput } from './LookupInput';

interface Props {
  def: FieldDef; meta?: EntityMeta; mf?: MetaField; value: unknown; record: Rec;
  mode: 'display' | 'edit' | 'create';
  onChange(patch: Rec): void;
}

/** Picks a Fiori control from the designer's FieldDef + Service Layer metadata kind. */
export function FieldControl({ def, meta, mf, value, record, mode, onChange }: Props) {
  if (def.template) return <Text style={{ minHeight: '1.5rem', display: 'inline-block' }}>{interpolate(def.template, record) || '—'}</Text>;
  const readonly = mode === 'display' || def.readonly || def.control === 'readonly' || (mode === 'edit' && mf?.isKey);
  const set = (v: unknown) => onChange({ [def.field]: v });
  const en = enumFor(meta, mf);
  const control = def.control && def.control !== 'auto' ? def.control
    : def.lookup ? 'lookup' : en || mf?.validValues ? 'select' : mf?.kind === 'bool' ? 'checkbox'
    : mf?.kind === 'date' || mf?.kind === 'datetime' ? 'date' : mf?.kind === 'int' || mf?.kind === 'decimal' ? 'number' : 'input';

  if (readonly) {
    const shown = en ? enumLabel(String(value ?? '')) : mf?.validValues ? (mf.validValues.find(v => v.value === value)?.description ?? String(value ?? '')) : formatValue(value, { field: def.field }, mf);
    return <Text style={{ minHeight: '1.5rem', display: 'inline-block' }}>{shown || '—'}</Text>;
  }

  switch (control) {
    case 'lookup':
      return <LookupInput lookup={def.lookup!} value={value} placeholder={def.placeholder}
        onSelect={(row, key) => { const patch: Rec = { [def.field]: key || null }; if (row && def.lookup!.copy) for (const [t, s] of Object.entries(def.lookup!.copy)) patch[t] = row[s]; onChange(patch); }} />;
    case 'select':
      if (en) return (
        <Select style={{ width: '100%' }} onChange={e => set(e.detail.selectedOption.dataset.v)}>
          {!def.required && <Option data-v="" selected={value == null || value === ''}></Option>}
          {en.members.map(m => <Option key={m.name} data-v={m.name} selected={value === m.name}>{enumLabel(m.name)}</Option>)}
        </Select>);
      return (
        <Select style={{ width: '100%' }} onChange={e => set(e.detail.selectedOption.dataset.v)}>
          {!def.required && <Option data-v="" selected={value == null || value === ''}></Option>}
          {(mf?.validValues ?? []).map(v => <Option key={v.value} data-v={v.value} selected={value === v.value}>{v.description}</Option>)}
        </Select>);
    case 'checkbox':
      return <CheckBox checked={value === true || value === 'tYES'} onChange={e => set(mf?.kind === 'bool' ? e.target.checked : e.target.checked ? 'tYES' : 'tNO')} />;
    case 'date':
      return <DatePicker formatPattern="yyyy-MM-dd" value={value ? String(value).slice(0, 10) : ''} style={{ width: '100%' }} onChange={e => set(e.detail.value || null)} />;
    case 'number':
      return <Input type="Number" value={value == null ? '' : String(value)} style={{ width: '100%' }} onChange={e => set(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'textarea':
      return <TextArea value={String(value ?? '')} rows={3} growing style={{ width: '100%' }} onInput={e => set(e.target.value)} />;
    default:
      return <Input value={String(value ?? '')} placeholder={def.placeholder} maxlength={mf?.maxLength} style={{ width: '100%' }} onInput={e => set(e.target.value)} />;
  }
}
